import { Box, TableCell } from "@mui/material";

import { SkeletonRounded } from "../../misc";
import { BODY_CELL_SX, LEADING_COL_WIDTH, RowDivider, StyledTableRow, colFlexAlign, getCellInnerSx } from "../cell";
import { useLayoutContext, useSelectionContext } from "../contexts";
import { TableColumn } from "../types";

interface ItemRowsSkeletonProps<TDetail> {
    columns: TableColumn<TDetail>[];
    rowCount?: number;
}

/**
 * Placeholder rendered by `ItemRows` when `isDetailsLoading && items.length === 0`.
 * Column-aligned skeleton bars so the loaded transition doesn't shift layout.
 */
export function ItemRowsSkeleton<TDetail>({ columns, rowCount = 2 }: ItemRowsSkeletonProps<TDetail>) {
    const { summaryColumnCount, leadingCells, divider, detailBackground } = useLayoutContext();
    const selection = useSelectionContext();
    const totalColSpan = summaryColumnCount + leadingCells;
    const hasCheckbox = !!selection;

    return (
        <>
            {Array.from({ length: rowCount }).map((_, di) => (
                <StyledTableRow key={`skeleton-${di}`} sx={{ position: "relative", backgroundColor: detailBackground }}>
                    {selection && (
                        <TableCell sx={{ ...BODY_CELL_SX, width: LEADING_COL_WIDTH }}>
                            <RowDivider color={divider} />
                            <Box
                                sx={getCellInnerSx({
                                    colIdx: 0,
                                    totalColSpan,
                                    isGrouped: true,
                                    hasCheckbox
                                })}
                            />
                        </TableCell>
                    )}
                    <TableCell sx={{ ...BODY_CELL_SX, width: LEADING_COL_WIDTH }}>
                        {!selection && <RowDivider color={divider} />}
                        <Box
                            sx={getCellInnerSx({
                                colIdx: leadingCells - 1,
                                totalColSpan,
                                isGrouped: true,
                                hasCheckbox
                            })}
                        />
                    </TableCell>
                    {columns.map((col, j) => {
                        const colIdx = j + leadingCells;
                        return (
                            <TableCell
                                key={col.dataIndex}
                                align={col.align ?? "left"}
                                width={col.width ?? "auto"}
                                sx={BODY_CELL_SX}
                            >
                                <Box
                                    sx={{
                                        ...getCellInnerSx({
                                            colIdx,
                                            totalColSpan,
                                            isGrouped: true,
                                            hasCheckbox
                                        }),
                                        justifyContent: colFlexAlign(col)
                                    }}
                                >
                                    <SkeletonRounded width="60%" height={16} />
                                </Box>
                            </TableCell>
                        );
                    })}
                </StyledTableRow>
            ))}
        </>
    );
}
